import type ArrayProxy from '@ember/array/proxy';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { restartableTask } from 'ember-concurrency';
import type CompanyModel from 'garaje/models/company';
import type DeskLocationModel from 'garaje/models/desk-location';
import type FeatureModel from 'garaje/models/feature';
import type LocationModel from 'garaje/models/location';
import type SubscriptionPlanModel from 'garaje/models/subscription-plan';
import type AjaxService from 'garaje/services/ajax';
import type BannerManagerService from 'garaje/services/banner-manager';
import type FeatureConfigService from 'garaje/services/feature-config';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type { AnySubscriptionModel, MaybeSubscriptionModel } from 'garaje/services/state';
import { APP, SUBSCRIPTION_SCOPE } from 'garaje/utils/enums';
import { TrackedArray } from 'tracked-built-ins';
import type { Constructor } from 'type-fest';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
function withSubscriptionStateFunctionality<TBase extends Constructor<any>>(Base: TBase) {
  class withSubscriptionStateFunctionality extends Base {
    declare visitorsSubscription?: MaybeSubscriptionModel;
    declare desksSubscription?: MaybeSubscriptionModel;
    declare deliveriesSubscription?: MaybeSubscriptionModel;
    declare roomsSubscription?: MaybeSubscriptionModel;
    declare workplaceSubscription?: MaybeSubscriptionModel;
    declare platformSubscription?: MaybeSubscriptionModel;
    declare reservationsSubscription?: MaybeSubscriptionModel;
    declare connectSubscription?: MaybeSubscriptionModel;
    declare emnoSubscription?: MaybeSubscriptionModel;
    declare vfdSubscription?: MaybeSubscriptionModel;
    declare screensSubscription?: MaybeSubscriptionModel;
    declare features?: FeatureModel | null;

    declare realDeliveriesSubscription?: AnySubscriptionModel;
    declare realRoomsSubscription?: AnySubscriptionModel;
    @tracked realDesksSubscription?: AnySubscriptionModel;

    @tracked hasAnySubscriptions = false;

    declare currentLocation?: LocationModel | null;
    declare currentCompany: CompanyModel | null;

    declare allPlans: ArrayProxy<SubscriptionPlanModel> | null;

    declare allDeskLocations: DeskLocationModel[];
    declare activeDeskLocations: DeskLocationModel[];

    declare _companyMeta: unknown;

    @service declare featureFlags: FeatureFlagsService;
    @service declare store: StoreService;
    @service declare ajax: AjaxService;
    @service declare bannerManager: BannerManagerService;
    @service declare featureConfig: FeatureConfigService;
    @service declare flashMessages: FlashMessagesService;

    initSubscriptionStateTask = restartableTask(
      async (
        incomingLocation?: LocationModel,
        preloadedSubscriptions?: AnySubscriptionModel[],
        preloadedFeatures?: FeatureModel | null,
      ) => {
        const { featureFlags, currentCompany } = this;

        if (!currentCompany) {
          return;
        }

        if (!this.currentLocation) {
          this.currentLocation = incomingLocation;
        }
        // Capture after the incomingLocation assignment so all three
        // branches see the same value.
        const location = this.currentLocation;

        // Precedence: locationBilling > MPB > default.
        if (featureFlags.isEnabled('locationBilling') && location) {
          await location.hasMany('locationSubscriptions').reload();
          const locationSubscriptions = (await location.locationSubscriptions).slice();

          this._assignSubscriptionFields(locationSubscriptions, { excludeFakedVisitors: false });
          [this.allPlans, this.features] = await Promise.all([
            this.store.findAll('subscription-plan'),
            this.store.queryRecord('feature', {
              filter: { company_id: currentCompany.id, location_id: location.id },
            }),
          ]);
        } else if (featureFlags.isEnabled('company-has-mixed-plan-billing') && location) {
          // Mirrors envoy-web's CompanyFeatures#billing_api_client.
          const subscriptions = (
            await this.store.query('scoped-subscription', {
              filter: {
                scope: SUBSCRIPTION_SCOPE.MIXED_PLAN,
                company_id: currentCompany.id,
                location_id: location.id,
              },
            })
          ).slice();

          this._assignSubscriptionFields(subscriptions, { excludeFakedVisitors: false });
          // allPlans is needed for LocationSubscription's
          // @alias('subscriptionPlan.*') getters when plans aren't sideloaded.
          [this.allPlans, this.features] = await Promise.all([
            this.store.findAll('subscription-plan'),
            this.store.queryRecord('feature', {
              filter: { company_id: currentCompany.id, location_id: location.id },
            }),
          ]);
        } else {
          let subscriptions = preloadedSubscriptions;
          if (!subscriptions) {
            const subscriptionsFilter = { filter: { company_id: currentCompany.id } };
            subscriptions = (await this.store.query('subscription', subscriptionsFilter)).slice();
          }

          this._assignSubscriptionFields(subscriptions, { excludeFakedVisitors: true });
          this.features = preloadedFeatures;
          if (this.features === undefined) {
            const featuresFilter = { filter: { company_id: currentCompany?.id } };
            this.features = await this.store.queryRecord('feature', featuresFilter);
          }
        }

        if (this.visitorsSubscription) {
          void this.checkRestrictionsTask.perform();
        }

        if (this.shouldLoadDeskLocations) {
          void this.setDeskLocationsTask.perform();
        }
      },
    );

    _assignSubscriptionFields(subscriptions: AnySubscriptionModel[], options: { excludeFakedVisitors: boolean }): void {
      const { featureFlags } = this;

      this.hasAnySubscriptions = subscriptions.length > 0;

      this.visitorsSubscription = options.excludeFakedVisitors
        ? subscriptions.find((sub) => sub.app === APP.VISITORS && !sub.faked)
        : subscriptions.find((sub) => sub.app === APP.VISITORS);
      this.deliveriesSubscription = subscriptions.find((sub) => sub.app === APP.DELIVERIES);
      this.roomsSubscription = subscriptions.find((sub) => sub.app === APP.ROOMS);
      this.desksSubscription = subscriptions.find((sub) => sub.app === APP.DESKS);
      this.workplaceSubscription = subscriptions.find((sub) => sub.app === APP.WORKPLACE);
      this.platformSubscription = subscriptions.find((sub) => sub.app === APP.PLATFORM);
      this.reservationsSubscription = subscriptions.find((sub) => sub.app === APP.RESERVATIONS);
      this.screensSubscription = featureFlags.isEnabled('wrkx-pricing-packaging')
        ? subscriptions.find((sub) => sub.app === APP.SCREENS)
        : // TODO: drop screens_devices fallback once wrkx-pricing-packaging is GA.
          subscriptions.find((sub) => sub.features.find((f) => f === 'screens_devices'));
      this.connectSubscription = subscriptions.find((sub) => sub.app === APP.CONNECT);
      this.emnoSubscription = subscriptions.find((sub) => sub.app === APP.EMNO);
      this.vfdSubscription = subscriptions.find((sub) => sub.app === APP.VFD);

      this.realDeliveriesSubscription = subscriptions.find((sub) => sub.app === APP.DELIVERIES && !sub.faked);
      this.realRoomsSubscription = subscriptions.find((sub) => sub.app === APP.ROOMS && !sub.faked);
      this.realDesksSubscription = subscriptions.find((sub) => sub.app === APP.DESKS && !sub.faked);
    }

    checkRestrictionsTask = restartableTask(async (refresh = true) => {
      const { currentCompany, visitorsSubscription } = this;

      if (!visitorsSubscription) {
        return;
      }

      const locationLimit = parseFloat(this.visitorsSubscription!.locationLimit);

      if (!visitorsSubscription.isSubscribed && locationLimit !== Infinity) {
        await this._getCompanyMeta.perform(currentCompany!, refresh);
      }
    });

    _getCompanyMeta = restartableTask(async (currentCompany: CompanyModel, refresh = false) => {
      if (this._companyMeta && !refresh) {
        return this._companyMeta;
      } else {
        const adapter = this.store.adapterFor('company');
        let meta = null;
        try {
          const url = `${adapter.buildURL('company', currentCompany.id)}?counts-meta=1`;
          const data = await this.ajax.request<{ meta: unknown }>(url);
          this._companyMeta = data.meta;
          meta = data.meta;
        } catch (_e) {
          // do nothing
        }
        return meta;
      }
    });

    setDeskLocationsTask = restartableTask(async () => {
      const { allDeskLocations, activeDeskLocations } = await this.getDeskLocations.perform();
      this.allDeskLocations = new TrackedArray(allDeskLocations);
      this.activeDeskLocations = new TrackedArray(activeDeskLocations);
    });

    /**
     * Whether desk-location data should be loaded for this company.
     * WRKX-5966: Also triggers for reservations-bundle customers (no standalone desks sub).
     */
    get shouldLoadDeskLocations(): boolean {
      if (!this.featureConfig.isEnabled('desks')) return false;
      if (this.features?.canAccessDesksApplication) return true;
      if (this.desksSubscription) return true;
      if (this.featureFlags.isEnabled('wrkx-pricing-packaging') && this.reservationsSubscription) return true;
      return false;
    }

    getDeskLocations = restartableTask(async () => {
      try {
        const adapterOptions = { companyId: this.currentCompany?.id };
        const deskLocations = await this.store.findAll('desk-location', { reload: true, adapterOptions });

        let allDeskLocations: DeskLocationModel[] = [];
        let activeDeskLocations: DeskLocationModel[] = [];

        if (deskLocations?.length) {
          allDeskLocations = deskLocations.slice();
          activeDeskLocations = allDeskLocations.filter((dl) => dl.active);
        }

        return { allDeskLocations, activeDeskLocations };
      } catch {
        this.flashMessages.showFlash(
          'error',
          'Some of our backend systems are currently down.  Desks-related features may not work properly.  This should be resolved soon.',
        );
        return { allDeskLocations: [], activeDeskLocations: [] };
      }
    });
  }

  return withSubscriptionStateFunctionality;
}

export default withSubscriptionStateFunctionality;
